import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useRef } from 'react';
import { format } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';
import { Match, League } from '@/lib/types';
import { useRealtimeLiveMatches } from './useRealtimeMatch';
import { logError } from '@/lib/errorHandling';

interface MatchesData {
  matches: Match[];
  leagues: League[];
}

// Constants for optimized polling
const STALE_TIME_DEFAULT = 60 * 1000; // 60 seconds for non-live data
const STALE_TIME_WITH_LIVE = 10 * 1000; // 10 seconds when live matches exist
const GC_TIME = 10 * 60 * 1000; // 10 minute cache
const POLL_INTERVAL_WITH_LIVE = 15 * 1000; // 15 seconds when live matches exist
const POLL_INTERVAL_NO_LIVE = 120 * 1000; // 2 minutes when no live matches

/**
 * Optimized hook for fetching matches for a specific date.
 * Smart polling that adjusts based on whether live matches exist.
 * CRITICAL: Includes current_minute in hash to ensure UI updates for live matches.
 */
export function useMatchesForDate(selectedDate: Date, enabled: boolean = true) {
  const queryClient = useQueryClient();
  const dateKey = format(selectedDate, 'yyyy-MM-dd');
  const lastMatchHashRef = useRef<string>('');
  
  const query = useQuery({
    queryKey: ['matches', dateKey],
    queryFn: async (): Promise<MatchesData> => {
      try {
        const dayStart = new Date(selectedDate);
        dayStart.setHours(0, 0, 0, 0);
        
        const dayEnd = new Date(selectedDate);
        dayEnd.setHours(23, 59, 59, 999);

        console.log(`[useMatchesForDate] Fetching matches for ${dateKey}`);

        const { data, error } = await supabase
          .from('matches')
          .select(`*, league:leagues(*)`)
          .gte('kickoff_time', dayStart.toISOString())
          .lte('kickoff_time', dayEnd.toISOString())
          .order('kickoff_time', { ascending: true });

        if (error) {
          console.error('[useMatchesForDate] Supabase error:', error);
          logError('useMatchesForDate', error, { date: dateKey, operation: 'fetch' });
          throw error;
        }

        // Handle null/undefined data gracefully
        const rawMatches = data ?? [];
        console.log(`[useMatchesForDate] Received ${rawMatches.length} matches`);

        const leagueMap = new Map<string, League>();
        const matches = rawMatches.map((m: any) => {
          if (m.league) {
            leagueMap.set(m.league.id, m.league);
          }
          return {
            ...m,
            status: m.status as Match['status'],
            statistics: m.statistics as Match['statistics'],
          } as Match;
        });

        // CRITICAL: Include current_minute and half_status in hash for live match updates
        // This ensures the UI updates when the minute changes, not just when scores change
        const matchHash = matches.map(m => {
          const minute = m.statistics?.current_minute ?? 0;
          const halfStatus = m.statistics?.half_status ?? '';
          return `${m.id}:${m.status}:${m.home_score}:${m.away_score}:${minute}:${halfStatus}`;
        }).join('|');
        
        // Only use cached data if hash truly matches (prevents unnecessary re-renders)
        if (matchHash === lastMatchHashRef.current && lastMatchHashRef.current !== '') {
          const cachedData = queryClient.getQueryData<MatchesData>(['matches', dateKey]);
          if (cachedData) {
            return cachedData;
          }
        }
        
        lastMatchHashRef.current = matchHash;

        return {
          matches,
          leagues: Array.from(leagueMap.values()).sort((a, b) => b.priority - a.priority),
        };
      } catch (error) {
        console.error('[useMatchesForDate] Error:', error);
        logError('useMatchesForDate', error, { date: dateKey, operation: 'fetch' });
        throw error;
      }
    },
    staleTime: (query) => {
      // Use shorter stale time when live matches exist
      const data = query.state.data;
      const hasLive = data?.matches?.some(m => m.status === 'live');
      return hasLive ? STALE_TIME_WITH_LIVE : STALE_TIME_DEFAULT;
    },
    gcTime: GC_TIME,
    // Dynamic polling based on live match status
    refetchInterval: (query) => {
      const data = query.state.data;
      const hasLive = data?.matches?.some(m => m.status === 'live');
      return hasLive ? POLL_INTERVAL_WITH_LIVE : POLL_INTERVAL_NO_LIVE;
    },
    refetchIntervalInBackground: false,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
    enabled,
    meta: {
      errorMessage: 'Failed to load matches',
    },
  });

  // Real-time updates for score/status changes - includes statistics for live minute updates
  const handleRealtimeUpdate = useCallback((match: Partial<Match>) => {
    if (match.status === 'live' || match.status === 'finished') {
      // Optimistically update the specific match in cache including statistics
      queryClient.setQueryData<MatchesData>(['matches', dateKey], (oldData) => {
        if (!oldData) return oldData;
        
        const updatedMatches = oldData.matches.map(m => {
          if (m.id === match.id) {
            return {
              ...m,
              ...match,
              // Merge statistics to preserve live data
              statistics: match.statistics ? { ...m.statistics, ...match.statistics } : m.statistics,
            };
          }
          return m;
        });
        
        return {
          ...oldData,
          matches: updatedMatches,
        };
      });
      
      // Also invalidate to ensure fresh data on next poll
      queryClient.invalidateQueries({ queryKey: ['matches', dateKey] });
    }
  }, [queryClient, dateKey]);

  useRealtimeLiveMatches(handleRealtimeUpdate);

  // Computed values
  const hasLiveMatches = useMemo(() => 
    query.data?.matches?.some(m => m.status === 'live') ?? false,
    [query.data?.matches]
  );

  return {
    ...query,
    hasLiveMatches,
  };
}
